import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import Table from '../../../common/components/Table/Table';
import { ContextMenuAction } from '../../../common/components/Table/types';
import { useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import {
  UploadContainer,
  PageTitle,
  UploadButton,
  ChooseFileButton,
  MainContent,
  TableContainer,
  TableTitle,
  StyledTabs,
  StyledTab,
  FilterContainer,
  SectionDivider,
  UploadSection,
  TablesSection,
  LoadingContainer,
  ErrorContainer,
  SuccessContainer,
} from './NO3SignedForms.styled';
import { useNO3SignedForms } from './hooks/useNO3SignedForms';
import { useNO3Download } from './hooks/useNO3Download';

const NO3SignedForms: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission(
    '/eno1',
    '/no3-signed-forms'
  );
  const { data: userData, isLoading: userLoading } = useUser();

  // Local state
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'error' | 'success' | 'info' | 'warning';
  }>({ open: false, message: '', type: 'success' });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get current user's designated operator
  const currentUserDO = userData?.designatedOperator || '';

  // Hook for API calls
  const {
    handleUpload,
    isUploading,
    uploadError,
    uploadSuccess,
    inboundData,
    inboundTotalCount,
    inboundLoading,
    outboundData,
    outboundTotalCount,
    outboundLoading,
    refreshData,
  } = useNO3SignedForms({
    inboundPpo: currentUserDO,
    outboundPpo: currentUserDO,
    page,
    size: pageSize,
  });

  // Download hook
  const downloadMutation = useNO3Download({
    onSuccess: () => {
      setNotification({
        open: true,
        message: t('no3SignedForms.downloadSuccess', 'File downloaded successfully!'),
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('Download error:', error);
      setNotification({
        open: true,
        message: t('no3SignedForms.downloadError', 'Failed to download file. Please try again.'),
        type: 'error',
      });
    },
  });

  // Define columns for the tables
  const tableColumns = useMemo(
    () => [
      {
        Header: t('table.id', 'ID'),
        accessor: 'id',
        width: '80px',
      },
      {
        Header: t('table.inboundPPO', 'Inbound PPO'),
        accessor: 'inboundPPO',
        width: '120px',
      },
      {
        Header: t('table.outboundPPO', 'Outbound PPO'),
        accessor: 'outboundPPO',
        width: '120px',
      },
      {
        Header: t('table.accountingYear', 'Accounting Year'),
        accessor: 'accountingYear',
        width: '130px',
      },
      {
        Header: t('table.mailCategory', 'Mail Category'),
        accessor: 'mailCategory',
        width: '120px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.format', 'Format'),
        accessor: 'format',
        width: '100px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.accountingType', 'Accounting Type'),
        accessor: 'accountingType',
        width: '130px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.uploaded', 'Uploaded'),
        accessor: 'uploated',
        width: '100px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.removalPPO', 'Removal PPO'),
        accessor: 'removalDo',
        width: '120px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.removalDate', 'Removal Date'),
        accessor: 'removalDate',
        width: '130px',
        Cell: ({ value }: { value: string | null }) => value,
      },
      {
        Header: t('table.fileName', 'File Name'),
        accessor: 'fileName',
        width: '250px',
      },
    ],
    [t]
  );

  // Define custom context menu actions
  const customContextMenuActions = useMemo(() => [
    {
      action: 'download' as ContextMenuAction,
      label: t('no3SignedForms.contextMenu.download', 'Download'),
      icon: <CloudDownloadIcon />,
    },
    {
      action: 'delete' as ContextMenuAction,
      label: t('no3SignedForms.contextMenu.delete', 'Delete'),
      icon: <DeleteIcon />,
    },
  ], [t]);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((action: ContextMenuAction, row: any) => {
    switch (action) {
      case 'download':
        if (row.id) {
          downloadMutation.mutate(row.id);
        } else {
          setNotification({
            open: true,
            message: t('no3SignedForms.noIdError', 'Cannot download: No file ID found.'),
            type: 'error',
          });
        }
        break;
      case 'delete':
        // Delete functionality - not implemented in backend yet
        setNotification({
          open: true,
          message: t('no3SignedForms.deleteNotImplemented', 'Delete functionality will be available soon.'),
          type: 'info',
        });
        break;
      default:
        console.log(`Action '${action}' not implemented`);
    }
  }, [downloadMutation, t, setNotification]);

  // Handle file selection
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const success = await handleUpload(selectedFile);
      if (success) {
        setNotification({
          open: true,
          message: t('upload.success', 'File uploaded successfully!'),
          type: 'success',
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh tables
        refreshData();
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  }, [selectedFile, handleUpload, t, refreshData]);

  // Handle choose file click
  const handleChooseFileClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, []);

  // Handle pagination
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size
  }, []);

  // Handle sidebar toggle
  const handleSidebarToggle = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Handle subtitle click
  const handleSubtitleClick = useCallback(() => {
    navigate('/eno1');
  }, [navigate]);

  // Handle notification close
  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  // Show loading state while checking permissions or user data
  if (permissionLoading || userLoading) {
    return (
      <LoadingContainer data-testid='no3-signed-forms-loading'>
        <CircularProgress data-testid='no3-signed-forms-loading-spinner' />
        <Typography>{t('common.loading', 'Loading...')}</Typography>
      </LoadingContainer>
    );
  }

  // Show unauthorized message if the user doesn't have permission
  if (!hasPermission) {
    return (
      <LoadingContainer data-testid='no3-signed-forms-unauthorized'>
        <Typography variant='h6' color='error' data-testid='no3-signed-forms-unauthorized-message'>
          {t('common.noPermission', "You don't have permission to access this page.")}
        </Typography>
      </LoadingContainer>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='no3-signed-forms-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='NO3 Signed Forms'
            subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            onSubtitleClick={handleSubtitleClick}
            isSidebarOpen={sidebarOpen}
            showFilters={{}}
            data-testid='no3-signed-forms-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='no3-signed-forms-sidebar'
              />
            </Box>
          )}

          {/* MainContent */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='no3-signed-forms-main-content'>
            {/* Upload Section */}
            <UploadSection>
              <UploadContainer data-testid='no3-signed-forms-upload-container'>
                <PageTitle data-testid='no3-signed-forms-title'>
                  {t('no3SignedForms.title', 'NO3 Signed Forms Upload')}
                </PageTitle>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    gap: 2,
                  }}
                  data-testid='no3-signed-forms-upload-ui'
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                    data-testid='no3-signed-forms-button-group'
                  >
                    <input
                      type='file'
                      id='no3-file-upload'
                      ref={fileInputRef}
                      hidden
                      onChange={handleFileChange}
                      accept='.pdf,.doc,.docx,.jpg,.jpeg,.png'
                      disabled={isUploading}
                      data-testid='no3-signed-forms-file-input'
                    />
                    <ChooseFileButton
                      variant='outlined'
                      disabled={isUploading}
                      onClick={handleChooseFileClick}
                      data-testid='no3-signed-forms-choose-file-button'
                    >
                      {t('upload.chooseFile', 'Choose File')}
                    </ChooseFileButton>

                    <UploadButton
                      variant='contained'
                      onClick={handleFileUpload}
                      disabled={!selectedFile || isUploading}
                      data-testid='no3-signed-forms-upload-button'
                    >
                      {isUploading
                        ? t('upload.uploading', 'UPLOADING...')
                        : t('upload.upload', 'UPLOAD')}
                    </UploadButton>
                  </Box>

                  {selectedFile && !isUploading && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                      data-testid='no3-signed-forms-selected-file-info'
                    >
                      <Typography
                        sx={{ fontSize: '14px', fontWeight: 500, mr: 1 }}
                        data-testid='no3-signed-forms-selected-file-label'
                      >
                        {t('upload.selectedFile', 'Selected file:')}
                      </Typography>
                      <Typography
                        sx={{ fontSize: '14px', color: '#00B6BA' }}
                        data-testid='no3-signed-forms-selected-file-name'
                      >
                        {selectedFile.name}
                      </Typography>
                    </Box>
                  )}

                  {isUploading && (
                    <LoadingContainer
                      sx={{ minHeight: 'auto', py: 2 }}
                      data-testid='no3-signed-forms-uploading'
                    >
                      <CircularProgress
                        size={24}
                        sx={{ color: '#00B6BA' }}
                        data-testid='no3-signed-forms-upload-progress'
                      />
                      <Typography
                        variant='body2'
                        sx={{ fontWeight: 500 }}
                        data-testid='no3-signed-forms-uploading-text'
                      >
                        {t('upload.uploading', 'Uploading file... Please wait')}
                      </Typography>
                    </LoadingContainer>
                  )}

                  {uploadError && (
                    <ErrorContainer data-testid='no3-signed-forms-upload-error'>
                      <Typography sx={{ fontSize: '14px', color: '#d32f2f' }}>
                        {uploadError}
                      </Typography>
                    </ErrorContainer>
                  )}
                </Box>
              </UploadContainer>
            </UploadSection>

            <SectionDivider />

            {/* Tables Section */}
            <TablesSection>
              {/* Inbound Table */}
              <TableContainer data-testid='no3-signed-forms-inbound-container'>
                <TableTitle data-testid='no3-signed-forms-inbound-title'>
                  {t('no3SignedForms.inboundTable', `Inbound from * to ${currentUserDO}`)}
                </TableTitle>
                {inboundLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="inbound-loading-indicator">
                    <CircularProgress data-testid="inbound-loading-spinner" />
                  </Box>
                ) : inboundData && inboundData.length > 0 ? (
                  <Table
                    data={inboundData}
                    columns={tableColumns}
                    totalCount={inboundTotalCount}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={false}
                    moduleName=''
                    viewMode={true}
                    rowBackgroundColor="rgb(230, 245, 230)"
                    customActions={customContextMenuActions}
                    rowEventHandlers={{
                      onContextMenuAction: handleContextMenuAction,
                    }}
                    data-testid='no3-signed-forms-inbound-table'
                  />
                ) : (
                  <NoDataFound
                    title={t('no3SignedForms.noInboundData', 'No Inbound Data Found')}
                    message={t('no3SignedForms.noInboundDataMessage', 'No inbound NO3 signed forms were found. Upload files to see them appear here.')}
                    showClearButton={false}
                    showResetButton={false}
                    data-testid='no3-signed-forms-inbound-no-data'
                  />
                )}
              </TableContainer>

              {/* Outbound Table */}
              <TableContainer data-testid='no3-signed-forms-outbound-container'>
                <TableTitle data-testid='no3-signed-forms-outbound-title'>
                  {t('no3SignedForms.outboundTable', `Outbound from ${currentUserDO} to *`)}
                </TableTitle>
                {outboundLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="outbound-loading-indicator">
                    <CircularProgress data-testid="outbound-loading-spinner" />
                  </Box>
                ) : outboundData && outboundData.length > 0 ? (
                  <Table
                    data={outboundData}
                    columns={tableColumns}
                    totalCount={outboundTotalCount}
                    page={page}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={false}
                    moduleName=''
                    viewMode={true}
                    rowBackgroundColor="rgb(255, 240, 240)"
                    customActions={customContextMenuActions}
                    rowEventHandlers={{
                      onContextMenuAction: handleContextMenuAction,
                    }}
                    data-testid='no3-signed-forms-outbound-table'
                  />
                ) : (
                  <NoDataFound
                    title={t('no3SignedForms.noOutboundData', 'No Outbound Data Found')}
                    message={t('no3SignedForms.noOutboundDataMessage', 'No outbound NO3 signed forms were found. Upload files to see them appear here.')}
                    showClearButton={false}
                    showResetButton={false}
                    data-testid='no3-signed-forms-outbound-no-data'
                  />
                )}
              </TableContainer>
            </TablesSection>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.type}
        onClose={handleNotificationClose}
        data-testid='no3-signed-forms-notification'
      />
    </Box>
  );
};

export default NO3SignedForms;
